import isEmpty from "lodash/isEmpty";
import snakeCase from "lodash/snakeCase";
import type { useTranslations } from "next-intl";

import { RunStatus } from "@zapier/reporting/src/graphql-types";
import {
  MassActionFailureStatus,
  MassActionMessage,
  MassActionMessageBody,
  MassActionType,
} from "./types";
import {
  ChannelRollupQuery,
  RunActionMutationResultFragment as RunActionResult,
} from "./useMassAction.generated";

type StatusRollup = ChannelRollupQuery["channelRollup"];

export type MassActionTranslator = ReturnType<
  typeof useTranslations<"mass-action">
>;

/**
 * Link copy is resolved through the translator, so the link tables are built
 * per call rather than held as module constants.
 */
function getGlobalFailureLinks(
  t: MassActionTranslator
): Record<string, { text: string; href: string }> {
  return {
    mass_replay_down: {
      href: "https://status.zapier.com",
      text: t("links.zapier-status"),
    },
    plan_limit: {
      href: "/app/billing/plans",
      text: t("links.upgrade-now"),
    },
    payment_issue: {
      href: "/app/settings/billing",
      text: t("links.update-card"),
    },
  };
}

function getSeeDetailsLinks(t: MassActionTranslator) {
  return {
    plan_limit: {
      href: "/app/settings/billing",
      text: t("links.upgrade-now"),
    },
    stale_auth: {
      href: "/app/connections",
      text: t("links.reconnect-account"),
    },
  };
}

export function getChannelMessage({
  channel,
  id,
  statusResult,
  t,
  type,
}: {
  channel?: RunActionResult | undefined;
  id?: string | undefined;
  statusResult?: StatusRollup | undefined;
  t: MassActionTranslator;
  type?: MassActionType | undefined;
}): MassActionMessage | null {
  if (!channel) {
    if (type) {
      // If we have a type but not a channel it means we haven't
      // got the original channel creation query back but we have
      // sent a request
      if (type === "replay") {
        return { title: t("replaying-title") };
      } else {
        return {
          title: t("deleting-title"),
        };
      }
    }

    // Nothing has been run, no message to show
    return null;
  }

  if ((channel?.globalFailures || "") !== "" || channel?.globalFailureType) {
    // There was a global failure which takes precedence
    return getGlobalFailureMessage(t, channel);
  }

  // Ensure we aren't using results from a previous channel
  const statusRollup =
    statusResult?.id === channel.channel ? statusResult : undefined;

  // Combine channel creation results with channel status results
  const statusDict = combineStatuses(channel, statusRollup);
  // Pending run count
  const pending = (statusRollup ? statusDict.pending : channel.pending) || 0;
  // Get total run count
  const total =
    (statusRollup ? statusRollup?.total : channel.pending) +
    getChannelTotal(channel);

  if (type === "replay") {
    if (pending) {
      return {
        text: t("progress", { completed: total - pending, total }),
        title: t("replaying-title"),
      };
    }

    const { success, ...nonSuccess } = statusDict;
    // If this is all success then we don't need a body
    const allSuccess = isEmpty(nonSuccess);

    return {
      body: allSuccess ? undefined : massReplayMessage(t, statusDict, id),
      messageType: allSuccess ? "success" : success ? "info" : "error",
      title: t("replay-result-title", {
        count: total,
        outcome: allSuccess ? "replayed" : "attempted",
      }),
    };
  } else if (type === "delete") {
    if (pending) {
      return {
        text: t("progress", { completed: total - pending, total }),
        title: t("deleting-title"),
      };
    }

    const { deleted, ...nonSuccess } = statusDict;
    // If this is all success then we don't need a body
    const allSuccess = isEmpty(nonSuccess);

    return {
      body: allSuccess ? undefined : massDeleteMessage(t, statusDict),
      messageType: allSuccess ? "success" : deleted ? "info" : "error",
      title: t("delete-result-title", { count: total }),
    };
  }
  return null;
}

/**
 * Formats global failure message
 */
function getGlobalFailureMessage(
  t: MassActionTranslator,
  channel: RunActionResult
): MassActionMessage {
  // @ts-ignore
  const link = getGlobalFailureLinks(t)[channel.globalFailureType];

  return {
    link,
    messageType: "error",
    text: channel.globalFailures,
    title: t("error-title"),
  };
}

/**
 * Combines the channel creation result and channel status result
 * into a single status dictionary since the channel creation results
 * are not included in the channel status results
 */
function combineStatuses(
  channel: RunActionResult,
  statusRollup: StatusRollup | undefined
) {
  const statusDict = { ...channel.failures };

  (statusRollup?.statuses || []).map((status) => {
    statusDict[status.status] = (statusDict[status.status] || 0) + status.count;
  });

  return statusDict;
}

/**
 * Gets the total number of actions based on the channel results
 * and the channel status numbers
 */
function getChannelTotal(channel: RunActionResult) {
  const failures: Record<string, number> = channel.failures || {};
  return Object.values(failures).reduce(
    (acc: number, count: number) => acc + count,
    0
  );
}

/**
 * Gets the "See Runs" link details
 */
function getReplayLink(
  t: MassActionTranslator,
  status: string,
  id: string | undefined
) {
  // Try to use our predefined links if we can
  // @ts-ignore
  const predefinedLink = getSeeDetailsLinks(t)[status];
  if (predefinedLink) return predefinedLink;

  // If the status is an actual RunStatus then it means it wasn't a MassReplay
  // error so filter to the channel and that status
  // @ts-ignore
  if (RunStatus[status]) {
    return {
      href: `/app/history?channel=${id}&status=${snakeCase(status)}`,
      text: t("links.see-runs"),
    };
  }

  // This means it was a MassReplay error so we have to filter to the channel
  // and the "channelError" which is what is given to use back from the channel
  // creation call
  return {
    href: `/app/history?channel=${id}&channelError=${snakeCase(status)}`,
    text: t("links.see-runs"),
  };
}

/**
 * Formats Mass Replay Messages for every status
 */
function massReplayMessage(
  t: MassActionTranslator,
  statusDict: Record<string, number>,
  id: string | undefined,
  isFreePlan = false
): MassActionMessageBody[] {
  return Object.entries(statusDict)
    .map(([status, count]) => {
      const message = massReplayStatus(t, status, count, isFreePlan);
      if (message) {
        return { link: getReplayLink(t, status, id), message };
      }
      return null;
    })
    .filter(Boolean) as MassActionMessageBody[];
}

function massReplayStatus(
  t: MassActionTranslator,
  status: string,
  count: number,
  isFreePlan = false
) {
  switch (snakeCase(status)) {
    // successful replay
    case RunStatus.success:
      return t("replay-status.success", { count });

    // did not attempt to replay:
    case MassActionFailureStatus.paused_zap:
      return t("replay-status.paused_zap", { count });
    case MassActionFailureStatus.successful_task:
      return isFreePlan
        ? t("replay-status.successful_task_free_plan", { count })
        : t("replay-status.successful_task", { count });
    case MassActionFailureStatus.outstanding_task:
      return t("replay-status.outstanding_task", { count });
    case MassActionFailureStatus.diverged_zap:
      return t("replay-status.diverged_zap", { count });
    case MassActionFailureStatus.expired_task:
      return t("replay-status.expired_task", { count });
    case MassActionFailureStatus.unsuccessful_task:
      return t("replay-status.unsuccessful_task", { count });
    case MassActionFailureStatus.deleted_zap:
      return t("replay-status.deleted_zap", { count });
    case MassActionFailureStatus.playing_task:
      return t("replay-status.playing_task", { count });
    case MassActionFailureStatus.stale_auth:
      return t("replay-status.stale_auth", { count });
    case MassActionFailureStatus.plan_limit:
      return t("replay-status.plan_limit", { count });
    case MassActionFailureStatus.incorrect_access_level:
      return t("replay-status.incorrect_access_level", { count });

    // replayed, but failed:
    case RunStatus.error:
      return t("replay-status.error", { count });
    case RunStatus.halted:
      return t("replay-status.halted", { count });
    case RunStatus.filtered:
      return t("replay-status.filtered", { count });
    case RunStatus.delayed:
      return t("replay-status.delayed", { count });
    case RunStatus.scheduled:
      return t("replay-status.scheduled", { count });
    case RunStatus.throttled:
      return t("replay-status.throttled", { count });
    case RunStatus.held:
      return t("replay-status.held", { count });
    case MassActionFailureStatus.access_blacklisted_app:
      return t("replay-status.access_blacklisted_app", { count });
    case "":
      return;
  }
}

/**
 * Formats Mass Delete Messages for every status
 */
function massDeleteMessage(
  t: MassActionTranslator,
  statusDict: Record<string, number>
): MassActionMessageBody[] {
  return Object.entries(statusDict)
    .map(([status, count]) => {
      const message = massDeleteStatus(t, status, count);
      return message ? { message } : null;
    })
    .filter(Boolean) as MassActionMessageBody[];
}

function massDeleteStatus(
  t: MassActionTranslator,
  status: string,
  count: number
) {
  switch (snakeCase(status)) {
    // successful delete
    case "deleted":
      return t("delete-status.deleted", { count });

    // did not attempt to delete:
    case MassActionFailureStatus.expired_task:
      return t("delete-status.expired_task", { count });
    case MassActionFailureStatus.playing_task:
      return t("delete-status.playing_task", { count });
    case MassActionFailureStatus.incorrect_access_level:
      return t("delete-status.incorrect_access_level", { count });
  }
}
